import {
  afterNextRender,
  ChangeDetectionStrategy,
  Component,
  DestroyRef,
  effect,
  ElementRef,
  inject,
  input,
  output,
  PLATFORM_ID,
  viewChild,
} from '@angular/core';
import { isPlatformBrowser } from '@angular/common';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { fromEvent } from 'rxjs';

import { ButtonComponent } from '../button/button.component';

/**
 * Aviso modal BLOQUEANTE de UN SOLO CTA (sin checkbox de confirmación), reutilizable en checkout y en
 * el modal de precompra. Nace en un único sitio: el padre lo gobierna con `open` y reacciona a
 * `acknowledge` (p. ej. cambiando el método de envío a Correos). No se cierra con Escape ni con el
 * backdrop: no se puede continuar sin aceptar.
 *
 * Vive en `shared/ui` (no en `features/checkout`) para que el modal de precompra —que es `shared`—
 * pueda montarlo sin invertir la dependencia hacia `features`.
 *
 * Focus-trap y guardas SSR siguen a `blocking-notice-modal`. Diferencia clave de scroll-lock: RESTAURA
 * el `overflow` previo del body al cerrar (no lo fuerza a ''), porque se abre sobre un contexto que ya
 * puede tener el scroll bloqueado (el modal de precompra); resetear a '' reactivaría el scroll de fondo.
 */
@Component({
  selector: 'app-coverage-notice-dialog',
  imports: [ButtonComponent],
  templateUrl: './coverage-notice-dialog.component.html',
  styleUrl: './coverage-notice-dialog.component.css',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class CoverageNoticeDialogComponent {
  private readonly platformId = inject(PLATFORM_ID);
  private readonly destroyRef = inject(DestroyRef);

  readonly open = input<boolean>(false);
  readonly title = input<string>('');
  readonly body = input<string>('');
  readonly ctaLabel = input<string>('Entendido');
  readonly mascotSrc = input<string>('/images/logo/info.png');
  /** Dimensiones intrínsecas del PNG (evitan CLS/deformación). Default = info.png. */
  readonly mascotWidth = input<number>(481);
  readonly mascotHeight = input<number>(640);
  readonly acknowledge = output<void>();

  private readonly dialogRef = viewChild<ElementRef<HTMLElement>>('dialog');
  private previouslyFocusedElement: HTMLElement | null = null;
  private previousBodyOverflow = '';

  constructor() {
    // Guardar/restaurar foco + bloqueo de scroll del body (restaurando el valor previo), según `open`.
    effect(() => {
      const isOpen = this.open();
      if (!isPlatformBrowser(this.platformId)) return;

      if (isOpen) {
        this.previousBodyOverflow = document.body.style.overflow;
        document.body.style.overflow = 'hidden';
        this.previouslyFocusedElement =
          (document.activeElement as HTMLElement | null) ?? null;
        queueMicrotask(() => {
          const root = this.dialogRef()?.nativeElement;
          if (root) this.getFocusableElements(root)[0]?.focus();
        });
      } else {
        document.body.style.overflow = this.previousBodyOverflow;
        if (this.previouslyFocusedElement) {
          const el = this.previouslyFocusedElement;
          this.previouslyFocusedElement = null;
          queueMicrotask(() => {
            if (document.contains(el)) el.focus({ preventScroll: true });
          });
        }
      }
    });

    // Focus trap BLOQUEANTE: traga Escape y cicla Tab/Shift+Tab dentro del modal.
    afterNextRender(() => {
      fromEvent<KeyboardEvent>(document, 'keydown')
        .pipe(takeUntilDestroyed(this.destroyRef))
        .subscribe((e) => {
          if (!this.open()) return;

          if (e.key === 'Escape') {
            e.preventDefault();
            e.stopPropagation();
            return;
          }

          if (e.key !== 'Tab') return;

          const modal = this.dialogRef()?.nativeElement;
          if (!modal) return;

          const focusables = this.getFocusableElements(modal);
          if (focusables.length === 0) {
            e.preventDefault();
            return;
          }

          const first = focusables[0];
          const last = focusables[focusables.length - 1];
          const active = document.activeElement as HTMLElement | null;

          if (!active || !modal.contains(active)) {
            e.preventDefault();
            first.focus();
            return;
          }

          if (e.shiftKey && active === first) {
            e.preventDefault();
            last.focus();
          } else if (!e.shiftKey && active === last) {
            e.preventDefault();
            first.focus();
          }
        });
    });
  }

  protected onAcknowledge(): void {
    this.acknowledge.emit();
  }

  /** Devuelve los elementos enfocables dentro del modal en orden DOM. */
  private getFocusableElements(root: HTMLElement): HTMLElement[] {
    const selector = [
      'a[href]',
      'button:not([disabled])',
      'input:not([disabled])',
      'select:not([disabled])',
      'textarea:not([disabled])',
      '[tabindex]:not([tabindex="-1"])',
    ].join(',');
    return Array.from(root.querySelectorAll<HTMLElement>(selector)).filter(
      (el) => el.offsetParent !== null || el === document.activeElement,
    );
  }
}
